import EmailIcon from '@material-ui/icons/Email'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import PhoneInTalkIcon from '@material-ui/icons/PhoneInTalk'
import { ReactElement } from 'react'
import styled from 'styled-components'

import { FlexBox } from '../../components/Base/Base'
import { CopyToClipBoard } from '../../components/CopyToClipBoard/CopyToClipBoard'
import { Text } from '../../components/Text/Text'
import { formattedName } from '../../helpers/formatters'
import { TeamV3 } from '../../models/team'
import { DriverStatus, FormattedUserRoles, User } from '../../models/user'
import { blueGray, darkGray, lightGray1, marigoldOrange, midGray, successD1, white } from '../../theme/colors'

const MemberStatusSortOrder = {
  [DriverStatus.ACTIVE]: 1,
  [DriverStatus.ONBOARDING]: 2,
  [DriverStatus.ELIGIBLE]: 3,
  [DriverStatus.APPLYING]: 4,
  [DriverStatus.SUSPENDED_TEMPORARILY]: 5,
  [DriverStatus.DEACTIVATED]: 6,
  [DriverStatus.INACTIVE]: 7,
}

const MemberStatusColors = {
  [DriverStatus.ACTIVE]: successD1,
  [DriverStatus.ONBOARDING]: marigoldOrange,
  [DriverStatus.ELIGIBLE]: marigoldOrange,
  [DriverStatus.APPLYING]: marigoldOrange,
  [DriverStatus.SUSPENDED_TEMPORARILY]: lightGray1,
  [DriverStatus.DEACTIVATED]: lightGray1,
  [DriverStatus.INACTIVE]: lightGray1,
}

const MemberStatusHuman = {
  [DriverStatus.ACTIVE]: 'Active',
  [DriverStatus.ONBOARDING]: 'Onboarding',
  [DriverStatus.ELIGIBLE]: 'Eligible',
  [DriverStatus.APPLYING]: 'Applying',
  [DriverStatus.SUSPENDED_TEMPORARILY]: 'Temporarily Suspended',
  [DriverStatus.DEACTIVATED]: 'Deactivated',
  [DriverStatus.INACTIVE]: 'Inactive',
}

type UserOpenerType = (sidebarOpener: ReactElement, user: User) => ReactElement

interface MembersSectionProps {
  team: TeamV3
  userOpener?: UserOpenerType
}

export function MembersSection({ team, userOpener }: MembersSectionProps) {
  const memberSort = (a: User, b: User) => {
    if (!a.driver.status) return 1
    if (!b.driver.status) return -1
    return MemberStatusSortOrder[a.driver.status] - MemberStatusSortOrder[b.driver.status]
  }

  return (
    <FlexBox isFullWidth isColumn alignItems="stretch">
      {[...team.members].sort(memberSort).map((member) => (
        <MemberItem key={member.id} member={member} userOpener={userOpener} />
      ))}
    </FlexBox>
  )
}

function MemberItem({ member, userOpener }: { key?: string; member: User; userOpener?: UserOpenerType }) {
  const driverStatus =
    member.defaultTeamMembership?.driverStatus ??
    member.driver.status ??
    (member.defaultTeamMembership?.isActive && DriverStatus.ACTIVE)
  const driverStatusHuman = driverStatus && MemberStatusHuman[driverStatus]

  return (
    <MemberSectionItem>
      <FlexBox isFullWidth>
        <FlexBox isColumn gap="8px" flex={1}>
          <CopyToClipBoard text={formattedName(member)} textProps={{ variant: 'header3' }}>
            <Text variant="header3" color={darkGray}>
              {formattedName(member)}
            </Text>
          </CopyToClipBoard>
          {member.email && (
            <CopyToClipBoard text={member.email} textProps={{ variant: 'label2' }}>
              <FlexBox gap="12px">
                <StyledEmailIcon />
                <Text variant="label2">{member.email}</Text>
              </FlexBox>
            </CopyToClipBoard>
          )}
          {member.mobile && (
            <CopyToClipBoard text={member.mobile} textProps={{ variant: 'label2' }}>
              <FlexBox gap="12px">
                <StyledPhoneIcon />
                <Text variant="label2">{member.mobile}</Text>
              </FlexBox>
            </CopyToClipBoard>
          )}
        </FlexBox>

        <FlexBox isColumn gap="8px" alignItems="flex-end">
          {member.isTeamDriver && driverStatus && (
            <Text variant="header3" color={MemberStatusColors[driverStatus]}>
              {driverStatusHuman}
            </Text>
          )}

          <Text variant="header3" color={midGray}>
            {member.roles?.map((role) => FormattedUserRoles[role]).join(' • ')}
          </Text>
        </FlexBox>

        <UserOpenerContainer>
          {userOpener &&
            userOpener(
              <ArrowContainer>
                <KeyboardArrowRightIcon htmlColor={blueGray} />
              </ArrowContainer>,
              member
            )}
        </UserOpenerContainer>
      </FlexBox>
    </MemberSectionItem>
  )
}

const MemberSectionItem = styled(FlexBox)`
  padding: 24px 0 24px 40px;
  background-color: ${white};
  margin-bottom: 2px;
`

const StyledEmailIcon = styled(EmailIcon)`
  margin-top: -1px;
  color: ${blueGray};
  width: 20px;
  height: 20px;
`

const StyledPhoneIcon = styled(PhoneInTalkIcon)`
  margin-top: -1px;
  color: ${blueGray};
  width: 20px;
  height: 20px;
`

// Sidebar opener container for user needs to be full height-ed/width-ed
// so arrow container styles can be applied correctly
const UserOpenerContainer = styled(FlexBox)`
  width: 100px;
  height: calc(100% + 48px);
  margin: -24px 0;

  & > div {
    width: 100%;
    height: 100%;

    & > div {
      width: 100%;
      height: 100%;
    }
  }
`

const ArrowContainer = styled(FlexBox)`
  width: 100%;
  height: 100%;
  cursor: pointer;
  padding: 24px 40px 0 0;
  justify-content: flex-end;
`
